/**
 * @prettier
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useTranslation, Trans } from "react-i18next";

// Actions
import { isInIframe } from "../../services/shared/helpers";

// Components
import Header from "../../components/Header";
import CustomButton from "../../components/CustomButton";
import Navigator from "../../components/Navigator";

// Images
import logo from "../../assets/img/idpal_logo.svg";

// Alt Text
import { imageAlt } from "../../config/accessabilityRules";

const LinkText = ({ linkTo = "#", title = "", ...props }) => {
    return (
        <a
            href={linkTo}
            target="_blank"
            title={title}
            aria-label={{ title }}
            rel="noreferrer"
        >
            {props.children}
        </a>
    );
};

const GroupLinkStartPage = ({ branding = null, policies = {} }) => {
    const { t } = useTranslation();
    const primaryFocusRef = useRef();
    const [showCustomClientsLogo, setShowCustomClientsLogo] = useState(false);
    const [showPoweredByIDPal, setShowPoweredByIDPal] = useState(false);
    const [navigationState, setNavigationState] = useState({ action: "load" });
    const customClientsLogo = branding?.logo || null;
    const isStartSubmissionEnabled =
        process.env.REACT_APP_ALLOW_GROUP_LINK_START_SUBMISSION === "true";

    const handleSendLinkClick = () => {
        setNavigationState({
            action: "next",
        });
    };

    const handleStartSubmissionClick = () => {};

    const renderPoweredByIDPal = () => (
        <div className="identity-mark-section">
            <p className="identity-mark-text">{t("idpal_powered_by")}</p>
            <img className="small-logo" src={logo} alt="ID-Pal Logo" />
        </div>
    );

    const customLogo = useMemo(() => {
        if (branding?.logo) {
            return `data:image/png;base64,${branding.logo}`;
        } else {
            return null;
        }
    }, [branding?.logo]);

    useEffect(() => {
        primaryFocusRef.current.focus();
        if (branding?.whitelabel === 0) {
            setShowPoweredByIDPal(true);
        }
        if (customClientsLogo && !isInIframe()) {
            setShowCustomClientsLogo(true);
        }
    }, [customClientsLogo, branding?.whitelabel]);

    useEffect(() => {
        document.title = t("idpal_group_link_start_doc_title");
    }, [t]);

    return (
        <div className="root-layout-container group-link-start-page">
            <Header showLanguageSelect isLogoHidden />
            <section className="content-wrapper">
                <div className="grouplink-start-logo-container">
                    {showCustomClientsLogo ? (
                        <img
                            alt={imageAlt.startLogo}
                            className="custom-logo-image"
                            src={customLogo}
                        />
                    ) : (
                        <img
                            alt={t("idpal_id_pal_logo_alt")}
                            className="custom-logo-image"
                            src={logo}
                        />
                    )}
                </div>
                <div className="grouplink-start-content-container">
                    <div className="grouplink-start-content">
                        <h1
                            ref={primaryFocusRef}
                            tabIndex={-1}
                            className="grouplink-start-text-container"
                        >
                            {t(
                                isStartSubmissionEnabled
                                    ? "idpal_group_link_start_heading"
                                    : "idpal_group_link_start_heading_invite"
                            )}
                        </h1>
                        {isStartSubmissionEnabled && (
                            <h2 className="grouplink-start-description-text">
                                {t("idpal_group_link_start_description")}
                            </h2>
                        )}
                        <p className="grouplink-start-privacy-policy-text">
                            <Trans
                                i18nKey="idpal_group_link_terms_and_conditions_warning"
                                values={{
                                    sessionState:
                                        t("idpal_group_link_send_link_cta").toLowerCase(),
                                }}
                                components={{
                                    link1: (
                                        <LinkText
                                            linkTo={
                                                policies.terms_and_conditions
                                            }
                                            title={t(
                                                "idpal_terms_and_conditions"
                                            )}
                                        />
                                    ),
                                    link2: (
                                        <LinkText
                                            linkTo={policies.privacy_policy}
                                            title={t("idpal_here")}
                                        />
                                    ),
                                }}
                            />
                        </p>
                    </div>

                    <div className="grouplink-start-cta-container">
                        <CustomButton
                            label={t("idpal_group_link_send_link_cta")}
                            id="send-link-cta-button"
                            handleClick={handleSendLinkClick}
                            aria-label={t("idpal_group_link_send_link_cta")}
                        />
                        {isStartSubmissionEnabled && (
                            <CustomButton
                                label={t(
                                    "idpal_group_link_start_submission_cta"
                                )}
                                id="group-link-start-cta-button"
                                handleClick={handleStartSubmissionClick}
                                outlined={true}
                                aria-label={t(
                                    "idpal_group_link_start_submission_cta"
                                )}
                            />
                        )}
                        {showPoweredByIDPal && renderPoweredByIDPal()}
                    </div>
                </div>
            </section>
            <Navigator
                page={"group_link_start_page"}
                action={navigationState.action}
                propsToPass={null}
            />
        </div>
    );
};

function mapStateToProps(state) {
    return {
        branding: state.config.profile?.data?.company_branding,
        policies: state.config.profile?.data?.policies,
    };
}

export default connect(mapStateToProps)(GroupLinkStartPage);
